import React, { useCallback, useEffect, useState } from "react";
import { Box, Link, Pagination, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import HomeService from "../../services/HomeService";
import Logger from "../../services/Logger";
import { trackEvent } from "../../services/GoogleAnalytics";
import { getContentLanguage } from "../../services/LangPrefs";
import { TEXT } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";
import {
    buildReadStoryPath,
    formatHomeStoryPublishedLabel,
    getHomeStoryRowId,
    getLocalizedStoryFromHomeStoryRo,
    truncateText,
} from "./storyRoUtils";

const PAGE_SIZE = 20;

/**
 * Crawlable list of original stories with permalinks to `/read-story/:id`.
 */
export default function StoriesIndexPage() {
    const { t } = usePagesTranslation();
    const contentLanguage = getContentLanguage();
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (pageIndex) => {
        setLoading(true);
        try {
            const result = await HomeService.getStoriesPage({
                page: pageIndex,
                pageSize: PAGE_SIZE,
                language: contentLanguage,
            });
            const content = Array.isArray(result.content) ? result.content : [];
            setRows(content);
            const tp = result.totalPages;
            setTotalPages(typeof tp === "number" && tp >= 1 ? tp : content.length > 0 ? 1 : 0);
        } catch (e) {
            Logger.error("StoriesIndexPage:", e);
            setRows([]);
            setTotalPages(0);
        }
        setLoading(false);
    }, [contentLanguage]);

    useEffect(() => {
        load(page);
    }, [load, page]);

    useEffect(() => {
        trackEvent("entered", { name: "StoriesIndexPage" });
    }, []);

    return (
        <Box
            component="main"
            sx={{
                width: "100%",
                maxWidth: 760,
                mx: "auto",
                px: { xs: 2, sm: 3 },
                py: { xs: 3, sm: 4 },
                boxSizing: "border-box",
            }}
        >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1, color: TEXT.primary }}>
                {t("stories.page-title", "Stories")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: TEXT.secondary, lineHeight: 1.7 }}>
                {t(
                    "stories.index-intro",
                    "Original illustrated stories from Puzzle Interact. Each tale is written for a crossword, word search, or hidden-message puzzle — not a scraped word list. Open a title to read the full text, then play.",
                )}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: TEXT.secondary }}>
                <Link component={RouterLink} to="/storybook" sx={{ fontWeight: 600, color: "inherit" }}>
                    {t("stories.open-storybook", "Open the storybook shelf")}
                </Link>
            </Typography>

            {loading ? (
                <Typography variant="body2" color="text.secondary">
                    {t("stories.loading", "Loading stories…")}
                </Typography>
            ) : rows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {t("stories.empty", "No stories match your filters yet.")}
                </Typography>
            ) : (
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                    {rows.map((row) => {
                        const id = getHomeStoryRowId(row);
                        const href = buildReadStoryPath(id);
                        if (!href) return null;
                        const { title, text } = getLocalizedStoryFromHomeStoryRo(row, contentLanguage);
                        const heading = (title || row?.templateTitle || "").trim() || t("stories.page-title", "Story");
                        const excerpt = truncateText(text, 180);
                        const published = formatHomeStoryPublishedLabel(row);
                        return (
                            <Box
                                key={id}
                                component="li"
                                sx={{
                                    mb: 2.5,
                                    pb: 2.5,
                                    borderBottom: `1px solid ${TEXT.disabled}33`,
                                }}
                            >
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    <Link
                                        component={RouterLink}
                                        to={href}
                                        sx={{ color: TEXT.primary, textDecoration: "underline" }}
                                    >
                                        {heading}
                                    </Link>
                                </Typography>
                                {published ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                                        {published}
                                    </Typography>
                                ) : null}
                                <Typography variant="body2" sx={{ color: TEXT.secondary, lineHeight: 1.65 }}>
                                    {excerpt || t("stories.preview-empty", "Open the story for the full text.")}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {totalPages > 1 && !loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={page + 1}
                        onChange={(_, v) => setPage(v - 1)}
                        color="primary"
                    />
                </Box>
            ) : null}
        </Box>
    );
}
